import React from "react";
import { ListCard, NormalText } from "@components/styles/dataDisplay";
import { useQuery } from "react-query";
import { fetchDocuments } from "@services/documents";
import { Button } from "nectar-ui";
import { DocumentItem } from "@components/pages/common/DocumentItem";
import { EmptyState } from "@components/EmptyState";
import { EmptyStateDocuments } from "@components/svgs";
import { CustomHeading, HeadingContainer, Subtitle, TitleContainer } from "@pages/style";
import { LawsuitInfo } from "@customTypes/responses/lawsuit";
import DocumentsTabSkeleton from "@pages/skeletons/CustomerDocumentsTabSkeleton";
import { MoreButtonContainer } from "@pages/LawsuitListPage/style";

type StateManagementProps = {
  lawsuitId: string;
  isLoadingLawsuit: boolean;
  isCase: boolean;
};

const StateManagement: React.FC<StateManagementProps> = ({
  lawsuitId,
  isLoadingLawsuit,
  isCase,
}) => {
  const [limit, setLimit] = React.useState(10);

  const { data, isSuccess, isLoading } = useQuery({
    queryKey: ["fetchDocuments", lawsuitId, limit],
    queryFn: () => fetchDocuments({ lawsuitId, limit }),
    keepPreviousData: true,
  });

  if (isLoading || isLoadingLawsuit) return <DocumentsTabSkeleton />;
  const lawsuitType = isCase ? "caso" : "processo";

  const { documents, count, total } = data ?? { documents: undefined, count: 0, total: 0 };

  if (isSuccess && documents && documents.length < 1)
    return (
      <EmptyState
        icon={<EmptyStateDocuments />}
        title={`Este ${lawsuitType} ainda não possui documentos`}
      >
        <NormalText noMargin>
          Quando o advogado adicionar documentos ou links, eles aparecerão aqui.
        </NormalText>
      </EmptyState>
    );

  const hasNextPage = count < total;

  const handleFetchMore = () => {
    setLimit((currentLimit) => currentLimit + 10);
  };

  const plural = total > 1 ? "s" : "";

  return (
    <>
      <NormalText margin="xs" color="gray">
        {`Mostrando ${count} de ${total} ${lawsuitType}${plural}`}
      </NormalText>
      <ListCard border>
        {documents?.map((document) => (
          <DocumentItem
            key={`${document.documentName}/${document.updateDate}`}
            document={document}
          />
        ))}
      </ListCard>
      {hasNextPage && (
        <MoreButtonContainer>
          <Button onClick={handleFetchMore} color="primary" reverse>
            carregar mais documentos
          </Button>
        </MoreButtonContainer>
      )}
    </>
  );
};

type LawsuitDocumentsTabProps = {
  lawsuitId: string;
};

const LawsuitDocumentsTab: React.FC<LawsuitDocumentsTabProps> = ({ lawsuitId }) => {
  const { isLoading, data: lawsuit } = useQuery<LawsuitInfo>(["getCase", lawsuitId]);

  const isCase = lawsuit?.type === "CTE_CASE";

  return (
    <div>
      <HeadingContainer>
        <TitleContainer>
          <CustomHeading noMargin size="sm">
            Documentos
          </CustomHeading>
          <Subtitle color="gray" size="lg" noMargin>
            {`Veja todos os documentos e links vinculados ao seu ${isCase ? "caso" : "processo"}.`}
          </Subtitle>
        </TitleContainer>
      </HeadingContainer>
      <StateManagement lawsuitId={lawsuitId} isLoadingLawsuit={isLoading} isCase={isCase} />
    </div>
  );
};

export default LawsuitDocumentsTab;
